/**
 * «Asignación inicial» — the words for the codes the platform's reads carry that are NOT reason labels (ADR 9058, 9060, R1/R3).
 *
 * A planner reads «falta el costo puesto en destino», never `landed_cost`. Each table maps a code the platform publishes (an article's
 * or a cell's `outcome`, the objective's terms, the price basis, a job's state) to a catalogue key; a code this build has no words for
 * is not shown as a code: it reads «Motivo no descrito en esta versión» with the code behind an «id» button (`CodeWords`). Keys are
 * written out in full so `i18n:coverage` sees every one.
 *
 * **Reason codes are not here.** What bound a line, why an article is not allocated, why a store was left out or not served, what an
 * article lacks beyond the objective, and an article's notes are the platform's labels (`GET /launch/initial/reason-labels`,
 * `./reasons.ts`).
 */

import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { IdTag } from '@/planning';

export type Table = Readonly<Record<string, string>>;

/** A file's translator (the export writers hand one in), as the `t` the words are written for. */
export function translatorOf(say: (key: string, values?: Readonly<Record<string, string | number>>) => string): TFunction {
  return ((key: string, values?: Readonly<Record<string, string | number>>) => say(key, values)) as unknown as TFunction;
}

/** An article's verdict in a run. */
export const OUTCOME_KEY: Table = {
  allocated: 'launch.initial.outcome.allocated',
  not_allocable: 'launch.initial.outcome.not_allocable',
  not_selected: 'launch.initial.outcome.not_selected',
};

/** The three things a run did with an article × store. */
export const CELL_OUTCOME_KEY: Table = {
  served: 'launch.initial.cellOutcome.served',
  excluded: 'launch.initial.cellOutcome.excluded',
  not_served: 'launch.initial.cellOutcome.not_served',
};

/** The decision a line carries, in words (the platform's four verbs). */
export const DISPOSITION_KEY: Table = {
  approve: 'launch.initial.disposition.approve',
  adjust: 'launch.initial.disposition.adjust',
  reject: 'launch.initial.disposition.reject',
  defer: 'launch.initial.disposition.defer',
};

/** A yes or a no in a file's cell: the workbook says it in words, the CSV keeps `true` / `false`. */
export const BOOLEAN_KEY: Table = {
  true: 'export.boolean.yes',
  false: 'export.boolean.no',
};

const ISO_WEEK = /^(\d{4})-W(\d{2})$/;
const DAY_MS = 86_400_000;

/** The Monday of an ISO week (`2026-W46` → `2026-11-09`), or `null` for a text that is not one. Pure. */
export function isoWeekStart(week: string | null | undefined): string | null {
  const found = week === null || week === undefined ? null : ISO_WEEK.exec(week.trim());
  if (found === null) return null;
  const year = Number(found[1]);
  const number = Number(found[2]);
  if (number < 1 || number > 53) return null;
  // The week with 4 January is week 1; its Monday is the start of the count.
  const january4 = Date.UTC(year, 0, 4);
  const mondayOfWeekOne = january4 - (((new Date(january4).getUTCDay() + 6) % 7) * DAY_MS);
  return new Date(mondayOfWeekOne + (number - 1) * 7 * DAY_MS).toISOString().slice(0, 10);
}

/** The basis of an article's list price (the markdown's own rule). */
export const PRICE_BASIS_KEY: Table = {
  panel_regular_price: 'launch.initial.priceBasis.panel_regular_price',
  ticket_price_ex_vat: 'launch.initial.priceBasis.ticket_price_ex_vat',
};

/** What «similar articles» means for a run — the choices the run can be asked with. */
export const SIMILARITY_BASIS_KEY: Table = {
  sub_category: 'launch.initial.similarity.sub_category',
  sub_category_gender: 'launch.initial.similarity.sub_category_gender',
  category: 'launch.initial.similarity.category',
};

/** A run's own status. */
export const RUN_STATUS_KEY: Table = {
  completed: 'launch.initial.runStatus.completed',
  refused: 'launch.initial.runStatus.refused',
};

/** The job's state before the run is written. */
export const JOB_STATE_KEY: Table = {
  queued: 'launch.initial.jobState.queued',
  running: 'launch.initial.jobState.running',
  succeeded: 'launch.initial.jobState.succeeded',
  failed: 'launch.initial.jobState.failed',
  expired: 'launch.initial.jobState.expired',
};

/** Why a whole run was refused: what the platform found it could not judge. */
export const RUN_REFUSAL_KEY: Table = {
  sources_unwired: 'launch.initial.refusal.sources_unwired',
  simulation_parameters_incomplete: 'launch.initial.refusal.simulation_parameters_incomplete',
  'launch.initial.solve_budget_missing': 'launch.initial.refusal.solve_budget_missing',
  'launch.initial.provenance_missing': 'launch.initial.refusal.provenance_missing',
  'launch.initial.parameter_value': 'launch.initial.refusal.parameter_value',
};

/** A job still moving; anything else — an unknown word included — has stopped. */
export function isInFlight(state: string | null | undefined): boolean {
  return state === 'queued' || state === 'running';
}

/** A code with a count after a colon (`capacity_unmeasured:12`): the code and the count. Pure. */
export function splitNote(note: string): { readonly code: string; readonly count: number | null } {
  const at = note.indexOf(':');
  if (at < 0) return { code: note, count: null };
  const count = Number(note.slice(at + 1));
  return { code: note.slice(0, at), count: Number.isFinite(count) ? count : null };
}

/** The catalogue key for `code` in `table`, or `null` when this build has no words for it. Pure. */
export function keyOf(table: Table, code: string | null | undefined): string | null {
  if (code === null || code === undefined) return null;
  return Object.prototype.hasOwnProperty.call(table, code) ? table[code] ?? null : null;
}

/** `code` in words, or `null` for a code this build has no words for. Pure. */
export function wordsOf(t: TFunction, table: Table, code: string | null | undefined, values: Readonly<Record<string, string | number>> = {}): string | null {
  const key = keyOf(table, code);
  return key === null ? null : t(key, values);
}

/**
 * A code in words; a code with none reads «Motivo no descrito en esta versión», the code behind «id»
 * (ADR 9060) — never the code as the text.
 */
export function CodeWords({ table, code, kind, values = {} }: {
  readonly table: Table;
  readonly code: string;
  /** What the code is, for the «id» button's name. */
  readonly kind: string;
  readonly values?: Readonly<Record<string, string | number>>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const key = keyOf(table, code);
  if (key !== null) return <span>{t(key, values)}</span>;
  return (
    <span className="inline-flex flex-wrap items-baseline gap-1">
      <span>{t('launch.initial.unclassified')}</span>
      <IdTag id={code} kind={kind} />
    </span>
  );
}

/** Several codes as words joined by «; » — a code with no words reads as the unclassified sentence once. Pure. */
export function listWords(t: TFunction, table: Table, codes: readonly string[]): string {
  const known = codes.map((code) => wordsOf(t, table, code)).filter((word): word is string => word !== null);
  const unknown = codes.length - known.length;
  return [...known, ...(unknown > 0 ? [t('launch.initial.unclassified')] : [])].join('; ');
}

